import React from 'react';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface ThemeToggleProps {
  /** Extra class names appended to the button (e.g. `theme-toggle--login`). */
  className?: string;
}

/**
 * A single button that flips between dark and light mode. It shows a sun while
 * dark mode is active (click to brighten) and a moon while light mode is active
 * (click to darken), mirroring the common convention of "the mode you'll get".
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({ className }) => {
  const { isLight, toggleTheme } = useTheme();
  const label = isLight ? 'Switch to dark mode' : 'Switch to light mode';

  return (
    <button
      type="button"
      className={`theme-toggle${className ? ` ${className}` : ''}`}
      onClick={toggleTheme}
      aria-label={label}
      title={label}
      aria-pressed={isLight}
      data-testid="theme-toggle"
    >
      {isLight ? (
        // Moon — currently light, click to go dark.
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        // Sun — currently dark, click to go light.
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path
            d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
};

export default ThemeToggle;
